import React from "react";

import {
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileSignature,
  RefreshCw,
} from "lucide-react";

import {
  formatSubscriptionDate,
  formatSubscriptionMoney,
  getAssociationName,
} from "../utils/subscriptionFormatters";

const COLUMNS = [
  {
    key: "anticipate",
    label: "À anticiper",
    description:
      "Échéance sous 60 jours",
    icon: CalendarClock,
    accent:
      "border-sky-300 dark:border-sky-800",
    iconStyle:
      "bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-300",
  },
  {
    key: "toPrepare",
    label: "Devis à préparer",
    description:
      "Zone d’alerte J-15",
    icon: Clock3,
    accent:
      "border-orange-300 dark:border-orange-800",
    iconStyle:
      "bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300",
  },
  {
    key: "quoteSent",
    label: "Devis envoyé",
    description:
      "En attente de réponse",
    icon: FileSignature,
    accent:
      "border-violet-300 dark:border-violet-800",
    iconStyle:
      "bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300",
  },
  {
    key: "followUp",
    label: "À relancer",
    description:
      "Aucune action récente",
    icon: RefreshCw,
    accent:
      "border-red-300 dark:border-red-800",
    iconStyle:
      "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
  },
  {
    key: "accepted",
    label: "Accepté",
    description:
      "Renouvellement validé",
    icon: CheckCircle2,
    accent:
      "border-emerald-300 dark:border-emerald-800",
    iconStyle:
      "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
];

function getDeadlineLabel(
  subscription,
) {
  const days =
    subscription.days_until_end;

  if (
    days === null ||
    days === undefined
  ) {
    return "Échéance inconnue";
  }

  if (days < 0) {
    const value =
      Math.abs(days);

    return `Expiré depuis ${value} jour${
      value > 1 ? "s" : ""
    }`;
  }

  if (days === 0) {
    return "Expire aujourd’hui";
  }

  return `Dans ${days} jour${
    days > 1 ? "s" : ""
  }`;
}

function RenewalCard({
  subscription,
  onEdit,
}) {
  const deadline =
    getDeadlineLabel(
      subscription,
    );

  const isLate =
    Number(
      subscription.days_until_end,
    ) < 0;

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-black text-slate-950 dark:text-white">
            {getAssociationName(
              subscription,
            )}
          </h3>

          <p className="mt-1 truncate text-xs font-medium text-slate-500 dark:text-slate-400">
            {
              subscription.product
            }
          </p>
        </div>

        <span className="shrink-0 text-sm font-black text-slate-950 dark:text-white">
          {formatSubscriptionMoney(
            subscription.total_amount,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            },
          )}
        </span>
      </div>

      <div className="mt-4 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
        <div className="flex items-center justify-between gap-3">
          <p
            className={[
              "text-xs font-black",
              isLate
                ? "text-red-600 dark:text-red-300"
                : "text-slate-700 dark:text-slate-200",
            ].join(" ")}
          >
            {deadline}
          </p>

          <p className="text-[11px] text-slate-400">
            {formatSubscriptionDate(
              subscription.end_date,
              "—",
            )}
          </p>
        </div>
      </div>

      {subscription.drive_quote_link && (
        <a
          href={
            subscription.drive_quote_link
          }
          target="_blank"
          rel="noreferrer"
          className="mt-3 block truncate text-xs font-bold text-violet-600 hover:underline dark:text-violet-300"
        >
          Voir le devis
        </a>
      )}

      <button
        type="button"
        onClick={() =>
          onEdit?.(
            subscription,
          )
        }
        className="mt-4 w-full rounded-xl bg-slate-950 px-3 py-2.5 text-xs font-black text-white transition hover:bg-indigo-700 dark:bg-white dark:text-slate-950 dark:hover:bg-indigo-200"
      >
        Modifier le dossier
      </button>
    </article>
  );
}

export default function SubscriptionRenewalKanban({
  pipeline = {},
  isLoading = false,
  onEdit,
}) {
  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-hidden">
        {[1, 2, 3, 4].map(
          (column) => (
            <div
              key={column}
              className="h-[600px] min-w-[300px] flex-1 animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800"
            />
          ),
        )}
      </div>
    );
  }

  return (
    <section className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">
          Renouvellements
        </p>

        <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
          Pipeline commercial
        </h2>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Visualise immédiatement
          où se trouve chaque
          renouvellement et les
          dossiers qui nécessitent
          une relance.
        </p>
      </div>

      <div className="overflow-x-auto pb-3">
        <div className="flex min-w-max gap-4">
          {COLUMNS.map(
            (column) => {
              const Icon =
                column.icon;

              const items =
                pipeline[
                  column.key
                ] || [];

              return (
                <section
                  key={
                    column.key
                  }
                  className={`w-[310px] shrink-0 overflow-hidden rounded-3xl border bg-slate-50/70 ${column.accent} dark:bg-slate-900/40`}
                >
                  <header className="border-b border-slate-200/70 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                      <div
                        className={`grid h-10 w-10 place-items-center rounded-xl ${column.iconStyle}`}
                      >
                        <Icon
                          size={
                            18
                          }
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-black text-slate-950 dark:text-white">
                            {
                              column.label
                            }
                          </h3>

                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {
                              items.length
                            }
                          </span>
                        </div>

                        <p className="mt-0.5 text-xs text-slate-400">
                          {
                            column.description
                          }
                        </p>
                      </div>
                    </div>
                  </header>

                  <div className="max-h-[650px] space-y-3 overflow-y-auto p-3">
                    {items.length ===
                    0 ? (
                      <div className="grid min-h-[150px] place-items-center rounded-2xl border border-dashed border-slate-200 bg-white/50 px-4 text-center dark:border-slate-700 dark:bg-slate-900/40">
                        <p className="text-xs font-semibold text-slate-400">
                          Aucun dossier
                        </p>
                      </div>
                    ) : (
                      items.map(
                        (
                          subscription,
                        ) => (
                          <RenewalCard
                            key={
                              subscription.id
                            }
                            subscription={
                              subscription
                            }
                            onEdit={
                              onEdit
                            }
                          />
                        ),
                      )
                    )}
                  </div>
                </section>
              );
            },
          )}
        </div>
      </div>
    </section>
  );
}